import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Form, FormControl, FormItem, FormLabel, FormMessage, useForm } from './Form';
import { Input } from './Input';
import { Button } from './Button';
import { Spinner } from './Spinner';
import { schema, postLogin, InputType } from '../endpoints/auth/login_with_password_POST.schema';
import { TwoFactorChallenge } from './TwoFactorChallenge';
import { OAuthLoginButton } from './OAuthLoginButton';
import { useAuth } from '../helpers/useAuth';
import styles from './PasswordLoginForm.module.css';

export type LoginFormData = InputType;

interface PasswordLoginFormProps {
  className?: string;
  redirectTo?: string;
}

export const PasswordLoginForm: React.FC<PasswordLoginFormProps> = ({
  className,
  redirectTo = '/',
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [twoFactorToken, setTwoFactorToken] = useState<string | null>(null);
  const [domainSso, setDomainSso] = useState<{ id: number; provider: string; displayName: string } | null>(null);
  const [lastCheckedDomain, setLastCheckedDomain] = useState('');
  const { onLogin } = useAuth();
  const navigate = useNavigate();

  // Auto-detect SSO config when user types their email
  const checkEmailDomain = useCallback((email: string) => {
    const atIdx = email.indexOf('@');
    if (atIdx < 0) return;
    const domain = email.slice(atIdx + 1).trim().toLowerCase();
    if (domain.length < 4 || !domain.includes('.') || domain === lastCheckedDomain) return;
    setLastCheckedDomain(domain);
    fetch(`/_api/auth/sso-lookup?email_domain=${encodeURIComponent(domain)}`)
      .then(r => r.json())
      .then((d: { sso: { id: number; provider: string; displayName: string } | null }) => {
        setDomainSso(d.sso);
      })
      .catch(() => setDomainSso(null));
  }, [lastCheckedDomain]);

  const form = useForm({
    defaultValues: {
      email: '',
      password: '',
    },
    schema,
  });

  const handleSubmit = async (data: LoginFormData) => {
    setError(null);
    setIsLoading(true);

    try {
      const result = await postLogin(data);

      // Check if 2FA is required
      if ((result as any).requires2FA && (result as any).tempToken) {
        setTwoFactorToken((result as any).tempToken);
        return;
      }

      onLogin(result.user);
      setTimeout(() => navigate(redirectTo), 200);
    } catch (err) {
      console.error('Login error:', err);
      setError(err instanceof Error ? err.message : 'Login failed. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  // Show 2FA challenge screen
  if (twoFactorToken) {
    return (
      <TwoFactorChallenge
        tempToken={twoFactorToken}
        onSuccess={() => {
          setTimeout(() => navigate(redirectTo), 200);
        }}
        onCancel={() => {
          setTwoFactorToken(null);
          setError(null);
        }}
      />
    );
  }

  return (
    <Form {...form}>
      <form
        onSubmit={form.handleSubmit(handleSubmit)}
        className={`${styles.form} ${className || ''}`}
      >
        {error && <div className={styles.errorMessage}>{error}</div>}

        <FormItem name="email">
          <FormLabel>Email</FormLabel>
          <FormControl>
            <Input
              placeholder="your@email.com"
              type="email"
              autoComplete="email"
              disabled={isLoading}
              value={form.values.email}
              onChange={(e) => form.setValues((prev) => ({ ...prev, email: e.target.value }))}
              onBlur={(e) => checkEmailDomain(e.target.value)}
            />
          </FormControl>
          <FormMessage />
        </FormItem>

        {domainSso && (
          <div style={{
            padding: '12px 16px', borderRadius: 8, marginBottom: 12,
            background: 'rgba(249,115,22,0.08)', border: '1px solid rgba(249,115,22,0.2)',
            textAlign: 'center',
          }}>
            <p style={{ fontSize: '0.8rem', color: 'var(--muted-foreground)', margin: '0 0 8px' }}>
              Your organization uses SSO
            </p>
            <OAuthLoginButton
              provider={domainSso.provider}
              ssoConfigId={domainSso.id}
              className={styles.submitButton}
            >
              Sign in with {domainSso.displayName}
            </OAuthLoginButton>
          </div>
        )}

        <FormItem name="password">
          <FormLabel>Password</FormLabel>
          <FormControl>
            <Input
              type="password"
              placeholder="••••••••"
              autoComplete="current-password"
              disabled={isLoading}
              value={form.values.password}
              onChange={(e) =>
                form.setValues((prev) => ({
                  ...prev,
                  password: e.target.value,
                }))
              }
            />
          </FormControl>
          <FormMessage />
        </FormItem>

        <Button type="submit" disabled={isLoading} className={styles.submitButton}>
          {isLoading ? (
            <span className={styles.loadingText}>
              <Spinner className={styles.spinner} size="sm" />
              Logging in...
            </span>
          ) : (
            'Log In'
          )}
        </Button>
      </form>
    </Form>
  );
};
